/**
 * Encuadre del mapa dentro de su hueco en la lámina. Geometría pura: sin canvas,
 * sin Leaflet, sin DOM.
 */

/**
 * Decide qué trozo del canvas fuente se dibuja y a qué tamaño, **garantizando
 * que el rectángulo obligatorio nunca se recorta**.
 *
 * Esta es la regla que distingue una lámina técnica de una foto decorativa: el
 * círculo de cobertura es la evidencia de la que depende el veredicto de
 * cumplimiento, así que un círculo cortado invalida el documento. Antes acá
 * había un ajuste *cover* —llenar el marco recortando lo que sobre—, que es lo
 * correcto para una portada y lo incorrecto para un plano.
 *
 * Estrategia, en orden:
 *   1. Ampliar el recorte hasta la proporción destino, tomando imagen real de
 *      alrededor (el canvas fuente tiene todo el viewport, no sólo el contenido).
 *   2. Si un lado topa el borde del canvas, recuperar lo que falte por el lado
 *      opuesto antes de rendirse.
 *   3. Si aun así no alcanza, *contain*: se achica el dibujo y quedan bandas de
 *      fondo. Se pierde algo de aire, nunca contenido.
 *
 * @param {{x0:number,y0:number,x1:number,y1:number}} must Rectángulo obligatorio.
 * @param {number} canvasW Ancho del canvas fuente.
 * @param {number} canvasH Alto del canvas fuente.
 * @param {number} destW Ancho del hueco disponible en la lámina.
 * @param {number} destH Alto del hueco disponible en la lámina.
 * @returns {{srcX:number, srcY:number, srcW:number, srcH:number, destW:number, destH:number}}
 */
export function fitCrop(must, canvasW, canvasH, destW, destH) {
  const aspect = destW / destH;
  let { x0, y0, x1, y1 } = must;

  /** Ensancha [a,b] hasta `target` dentro de [0, limit], compensando por el lado que pueda. */
  const widen = (a, b, target, limit) => {
    const add = (target - (b - a)) / 2;
    if (add <= 0) return [a, b];
    let lo = Math.max(0, a - add);
    let hi = Math.min(limit, b + add);
    // Un lado topó el borde: pedirle al otro lo que falta.
    let missing = target - (hi - lo);
    if (missing > 0) lo = Math.max(0, lo - missing);
    missing = target - (hi - lo);
    if (missing > 0) hi = Math.min(limit, hi + missing);
    return [lo, hi];
  };

  if ((x1 - x0) / (y1 - y0) < aspect) {
    [x0, x1] = widen(x0, x1, (y1 - y0) * aspect, canvasW);
  } else {
    [y0, y1] = widen(y0, y1, (x1 - x0) / aspect, canvasH);
  }

  const srcW = Math.max(20, x1 - x0);
  const srcH = Math.max(20, y1 - y0);
  // `min` = contain. Nunca `max`: eso sería cover, y cover corta.
  const scale = Math.min(destW / srcW, destH / srcH);

  return { srcX: x0, srcY: y0, srcW, srcH, destW: srcW * scale, destH: srcH * scale };
}
